import type {
	Appointment,
	AppointmentAvailability,
	AppointmentReminder,
	AppointmentSuggestedSlots,
	AppointmentType
} from "@/types/appointment"
import type { DateString, DateTimeString } from "@/types/common"
import { HttpClient, HUB_ROOT } from "@/api/httpClient"
import { dPayload } from "@/utils/dayjs"

export interface AppointmentsListQuery {
	from: DateString | Date
	to: DateString | Date
}

interface AppointmentsListParams {
	from: DateString
	to: DateString
}

export interface AppointmentPayload {
	/** YYYY-MM-DD HH:mm:ss */
	datetime: DateTimeString | Date
	title: string
	patient_id?: number
	office_id?: number
	draft?: boolean
	/** durata in minuti */
	duration?: number
	type: AppointmentType
	all_day: boolean
}

export interface AppointmentSuggestQuery {
	from: DateString | Date
	to: DateString | Date
	office_id: number
	/** durata in minuti */
	duration: number
}

export interface AppointmentCheckAvailabilityQuery {
	/** YYYY-MM-DD HH:mm:ss */
	slot: DateTimeString | Date
	/** durata in minuti */
	duration: number
	/** in modifica, esclude l'appuntamento corrente dal check overlap */
	exclude_appointment_id?: number
	office_id?: number
}

export interface MoveAppointmentsPayload {
	from_office: number
	to_office: number
}

export interface AppointmentRemindersPayload {
	reminders: AppointmentReminder[]
}

function toAppointmentPayload(payload: AppointmentPayload) {
	return {
		type: payload.type,
		all_day: payload.all_day ? 1 : 0,
		datetime: dPayload(payload.datetime, "datetime"),
		title: payload.title,
		...(payload.patient_id !== undefined ? { patient_id: payload.patient_id } : {}),
		...(payload.office_id !== undefined ? { office_id: payload.office_id } : {}),
		draft: payload.draft ? 1 : 0,
		...(payload.duration !== undefined ? { duration: payload.duration } : {})
	}
}

export default {
	list(payload: AppointmentsListQuery, signal?: AbortSignal) {
		const params: AppointmentsListParams = {
			from: dPayload(payload.from, "date"),
			to: dPayload(payload.to, "date")
		}

		return HttpClient.get<Appointment[]>(`${HUB_ROOT}/users/appointments`, { params, signal })
	},
	suggest(payload: AppointmentSuggestQuery) {
		return HttpClient.get<AppointmentSuggestedSlots[]>(`${HUB_ROOT}/users/appointments/suggest`, {
			params: {
				from: dPayload(payload.from, "date"),
				to: dPayload(payload.to, "date"),
				office_id: payload.office_id,
				duration: payload.duration
			}
		})
	},
	checkAvailability(payload: AppointmentCheckAvailabilityQuery) {
		return HttpClient.get<AppointmentAvailability>(`${HUB_ROOT}/users/appointments/check-availability`, {
			params: {
				slot: dPayload(payload.slot, "datetime"),
				duration: payload.duration,
				...(payload.exclude_appointment_id !== undefined
					? { exclude_appointment_id: payload.exclude_appointment_id }
					: {}),
				...(payload.office_id !== undefined ? { office_id: payload.office_id } : {})
			}
		})
	},
	details(appointmentId: number, filters?: { read?: boolean }) {
		const params: { read?: 0 | 1 } = {}

		if (filters?.read !== undefined) {
			params.read = filters.read ? 1 : 0
		}

		return HttpClient.get<Appointment>(`${HUB_ROOT}/users/appointments/${appointmentId}`, { params })
	},
	create(payload: AppointmentPayload) {
		return HttpClient.post<Appointment>(`${HUB_ROOT}/users/appointments`, toAppointmentPayload(payload))
	},
	update(appointmentId: number, payload: AppointmentPayload) {
		return HttpClient.put<Appointment>(
			`${HUB_ROOT}/users/appointments/${appointmentId}`,
			toAppointmentPayload(payload)
		)
	},
	move(payload: MoveAppointmentsPayload) {
		return HttpClient.post(`${HUB_ROOT}/users/appointments/move`, payload)
	},
	// TODO: verificare utilizzo di questo endpoint
	reminders(appointmentId: number, payload: AppointmentRemindersPayload) {
		return HttpClient.put(`${HUB_ROOT}/users/appointments/${appointmentId}/reminders`, payload)
	},
	delete(appointmentId: number) {
		return HttpClient.delete(`${HUB_ROOT}/users/appointments/${appointmentId}`)
	}
}
